import { useEffect, useMemo } from 'react'
import { useWorkspaceCategoryTypes } from '@opallabs/admin-configuration'
import { useReadBlockV3, useReadPlans } from '@opallabs/api'
import { type CascadingMenuOption } from '@opallabs/concepts'
import { useWorkspace } from '@opallabs/hooks'
import { type DeprecatedSingleSelectOption } from '@opallabs/lapidary'
import type { MomentData, Plan } from '../types'
import { selectBlockPlanStatus, selectPlans } from './selectors'
import { useSwimlaneOptionsForPlan } from './useSwimlaneOptions'

type UseAddToPlanDataArgs = {
  moments: MomentData[]
  selectedPlanItem: CascadingMenuOption | null
  setSelectedCategoryType: (category: DeprecatedSingleSelectOption) => void
}

type UseAddToPlanDataValue = {
  categoryTypeOptions: DeprecatedSingleSelectOption[]
  isInitialLoading: boolean
  isOnPlan?: boolean
  isSwimlanesLoading: boolean
  planTitle?: string
  plans?: Plan[]
  swimlaneOptions: CascadingMenuOption[]
}

export const useAddToPlanData = ({
  moments,
  selectedPlanItem,
  setSelectedCategoryType,
}: UseAddToPlanDataArgs): UseAddToPlanDataValue => {
  const { uuid: workspaceUuid } = useWorkspace()

  // For single-moment cases where isOnPlan isn't provided, fetch the paired block
  const pairedBlockId =
    moments.length === 1 ? moments[0].pairedBlockId : undefined
  const shouldFetchPairedBlock = Boolean(
    pairedBlockId && moments[0]?.isOnPlan === undefined
  )
  const { data: blockPlanStatus, isFetching: isBlockFetching } = useReadBlockV3(
    {
      workspaceId: workspaceUuid,
      blockId: pairedBlockId ?? undefined,
      query: {
        include: ['block_connectors.category', 'block_connectors.view'],
      },
      select: selectBlockPlanStatus,
      enabled: shouldFetchPairedBlock,
    }
  )

  const { data: categoryTypes, isLoading: isCategoryTypesLoading } =
    useWorkspaceCategoryTypes({
      queryKeyParams: {
        workspace_id: workspaceUuid,
        only_active: true,
      },
    })

  const { data: plans, isLoading: isPlansLoading } = useReadPlans({
    filter: {},
    workspaceUuid,
    select: selectPlans,
  })

  const {
    data: swimlaneOptions = [],
    isLoading: isSwimlaneOptionsLoading,
    isFetching: isSwimlaneOptionsFetching,
  } = useSwimlaneOptionsForPlan({
    planId: selectedPlanItem?.value,
    workspaceUuid,
  })

  const isInitialLoading =
    isCategoryTypesLoading ||
    isPlansLoading ||
    (shouldFetchPairedBlock && isBlockFetching)
  const isSwimlanesLoading = Boolean(
    selectedPlanItem && (isSwimlaneOptionsLoading || isSwimlaneOptionsFetching)
  )

  const categoryTypeOptions = useMemo<
    DeprecatedSingleSelectOption<string>[]
  >(() => {
    if (!categoryTypes) return []
    return categoryTypes.data
      .filter(
        (categoryType) =>
          categoryType.attributes.supported_paired_type === 'moment'
      )
      .map((categoryType) => ({
        value: categoryType.id,
        label: categoryType.attributes.name,
      }))
  }, [categoryTypes])

  // Ensure the selected category type is never null
  // If there is only one category type option, select it automatically
  // If there are more, the user is required to select one
  useEffect(() => {
    if (categoryTypeOptions?.length === 1) {
      setSelectedCategoryType(categoryTypeOptions[0])
    }
  }, [setSelectedCategoryType, categoryTypeOptions])

  return {
    categoryTypeOptions,
    isInitialLoading,
    isSwimlanesLoading,
    plans,
    swimlaneOptions,
    ...blockPlanStatus,
  }
}
